import Handsontable from 'handsontable';
import { ComponentType } from 'react';

import { dropdownRenderer } from '../materials/dropdowns/dropdown-in-excel';
import { DropdownEditor } from '../materials/dropdowns/dropdown-editor';
import { autocompleteRenderer } from '../materials/autocompletes/autocomplete-in-excel';
import { AutocompleteEditor } from '../materials/autocompletes/autocomplete-editor';
import { DatepickerEditor, datepickerRenderer } from '../materials/datepickers/datepicker-in-excel';
import { dateValidator, hourMinuteValidatorAllowEmpty } from '../../services/excel-table.service';

import { TimePickerComponent } from './time-picker';
import {
  createReactComponentEditorClass,
  createAutocompleteEditorClass,
  createDropdownEditorClass,
} from './component-editor-class';

import type { ComponentEditorProps } from './component-editor-class';

import { type SearchOption } from '@/types/search';
import { PLAN_FIELDS } from '@/lib/constants';
import {
  EPlanFormulationProductTypeLabel,
  EPlanMaterialTypeLabel,
  EPlanOrderStatusLabel,
  EPlanPlacingMethodLabel,
  EPlanQuantitySuffixLabel,
  EPlanVehicleSizeLabel,
} from '@/features/plans/types/plan.enum';

/**
 * Helper function to create dropdown column with canEnterAnotherValue
 * Automatically sets strict and allowInvalid based on canEnterAnotherValue
 * Uses DropdownEditor for custom popup with DialogCustom positioning
 * @param config - Column configuration
 * @returns Column configuration with strict and allowInvalid set automatically
 */
const createDropdownColumn = (config: {
  data: number;
  title: string;
  source: string[];
  width: number;
  canEnterAnotherValue?: boolean;
  maxLength?: number;
}) => {
  const canEnterAnotherValue = config.canEnterAnotherValue ?? false;
  const { maxLength, ...rest } = config;
  return {
    ...rest,
    ...(maxLength != null ? { maxLength } : {}),
    type: 'text' as const, // Changed from 'dropdown' to 'text' to use custom editor
    editor: createDropdownEditorClass(
      DropdownEditor as unknown as ComponentType<ComponentEditorProps>,
      (_col: number) => {
        // Return source options for this column
        return config.source;
      },
      (_col: number) => {
        // Return canEnterAnotherValue for this column
        return canEnterAnotherValue;
      }
    ),
    renderer: dropdownRenderer,
    canEnterAnotherValue,
    // Automatically set strict and allowInvalid based on canEnterAnotherValue
    strict: !canEnterAnotherValue,
    allowInvalid: canEnterAnotherValue,
  };
};

/**
 * Get column definitions for Handsontable
 * @param plantOptions - Plant options for autocomplete column
 * @param witnessCompanyOptions - Witness company options for autocomplete column
 * @returns Array of column definitions
 */
export const getHandsontableColumns = (
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = []
) => {
  return [
    createDropdownColumn({
      data: 0,
      title: 'ステータス',
      source: Object.values(EPlanOrderStatusLabel),
      width: 90,
      canEnterAnotherValue: false,
    }),
    {
      data: 1,
      title: 'プラント',
      type: 'text',
      width: 200,
      editor: createAutocompleteEditorClass(
        AutocompleteEditor as unknown as ComponentType<ComponentEditorProps>,
        (_col: number) => {
          // Column 1 is プラント (Plant)
          return plantOptions;
        }
      ),
      strict: false,
      allowInvalid: true,
      allowEmpty: true,
      renderer: autocompleteRenderer,
    },
    {
      data: 2,
      title: '打設日',
      type: 'text',
      width: 120,
      editor: createReactComponentEditorClass(
        DatepickerEditor as unknown as ComponentType<ComponentEditorProps>
      ),
      renderer: datepickerRenderer,
      validator: dateValidator,
      strict: true,
      allowInvalid: false,
      wordWrap: true,
    },
    {
      data: 3,
      title: '希望',
      type: 'text',
      width: 80,
      editor: createReactComponentEditorClass(
        TimePickerComponent as unknown as ComponentType<ComponentEditorProps>,
        PLAN_FIELDS.TIME_HOPE.maxLength
      ),
      validator: hourMinuteValidatorAllowEmpty,
      strict: true,
      wordWrap: true,
    },
    {
      data: 4,
      title: '可能',
      width: 80,
      editor: createReactComponentEditorClass(
        TimePickerComponent as unknown as ComponentType<ComponentEditorProps>,
        PLAN_FIELDS.TIME_POSSIBLE.maxLength
      ),
      validator: hourMinuteValidatorAllowEmpty,
      strict: true,
      wordWrap: true,
    },
    {
      data: 5,
      title: '可能時間補助',
      width: 120,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.TIME_POSSIBLE_REMARKS.maxLength,
      wordWrap: true,
    },
    {
      data: 6,
      title: '打設箇所',
      width: 150,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.PLACE_TO_PLACE.maxLength,
      wordWrap: true,
    },
    { data: 7, title: '数量', type: 'numeric', width: 150, wordWrap: true },
    createDropdownColumn({
      data: 8,
      title: '接尾辞',
      source: Object.values(EPlanQuantitySuffixLabel),
      width: 80,
      canEnterAnotherValue: true,
    }),
    {
      data: 9,
      title: '単位',
      width: 80,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.QUANTITY_UNIT.maxLength,
      wordWrap: true,
    },
    createDropdownColumn({
      data: 10,
      title: '種類',
      source: Object.values(EPlanFormulationProductTypeLabel),
      width: 100,
      canEnterAnotherValue: true,
    }),
    {
      data: 11,
      title: '内容',
      width: 120,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.FORMULATION_PRODUCT.maxLength,
      wordWrap: true,
    },
    createDropdownColumn({
      data: 12,
      title: '車両',
      source: Object.values(EPlanVehicleSizeLabel),
      width: 80,
      canEnterAnotherValue: true,
    }),
    createDropdownColumn({
      data: 13,
      title: '種類',
      source: Object.values(EPlanMaterialTypeLabel),
      width: 130,
      canEnterAnotherValue: true,
    }),
    { data: 14, title: '数量', type: 'numeric', width: 80 },
    {
      data: 15,
      title: '希望',
      width: 80,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.PITCH_HOPE.maxLength,
      wordWrap: true,
    },
    {
      data: 16,
      title: '可能',
      width: 80,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.PITCH_POSSIBLE.maxLength,
      wordWrap: true,
    },
    createDropdownColumn({
      data: 17,
      title: '打設手法',
      source: Object.values(EPlanPlacingMethodLabel),
      width: 100,
      canEnterAnotherValue: true,
      maxLength: PLAN_FIELDS.PLACING_METHOD.maxLength,
    }),
    {
      data: 18,
      title: 'その他',
      type: 'text',
      width: 200,
      editor: createAutocompleteEditorClass(
        AutocompleteEditor as unknown as ComponentType<ComponentEditorProps>,
        (_col: number) => {
          // Column 18 is その他 (Witness Company)
          return witnessCompanyOptions;
        }
      ),
      strict: false,
      allowInvalid: true,
      allowEmpty: true,
      renderer: autocompleteRenderer,
    },
    {
      data: 19,
      title: '氏名',
      width: 100,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.WITNESS_NAME.maxLength,
      wordWrap: true,
    },
    {
      data: 20,
      title: '連絡先',
      width: 120,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.WITNESS_TEL.maxLength,
      wordWrap: true,
    },
    {
      data: 21,
      title: '社内',
      width: 150,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.REMARKS_FOR_CLIENT.maxLength,
      wordWrap: true,
    },
    {
      data: 22,
      title: 'ゼネコン',
      width: 150,
      editor: 'maxLengthEditor',
      maxLength: PLAN_FIELDS.REMARKS.maxLength,
      wordWrap: true,
    },
  ];
};

/**
 * Get nested headers configuration for Handsontable
 * @returns Nested headers array
 */
export const getHandsontableNestedHeaders = () => {
  return [
    [
      { label: '', colspan: 1 },
      { label: '', colspan: 1 },
      { label: '', colspan: 1 },
      { label: '時間', colspan: 3 },
      { label: '', colspan: 1 },
      { label: '数量', colspan: 3 },
      { label: '商品', colspan: 2 },
      { label: '', colspan: 1 },
      { label: '先行材', colspan: 2 },
      { label: 'ピッチ', colspan: 2 },
      { label: '', colspan: 1 },
      { label: '立会', colspan: 3 },
      { label: '備考', colspan: 2 },
    ],
    [
      'ステータス',
      'プラント',
      '打設日',
      '希望',
      '可能',
      '可能時間補助',
      '打設箇所',
      '数量',
      '接尾辞',
      '単位',
      '種類',
      '内容',
      '車両',
      '種類',
      '数量',
      '希望',
      '可能',
      '打設手法',
      '会社',
      '氏名',
      '連絡先',
      '社内',
      'ゼネコン',
    ],
  ];
};

const TextEditor = Handsontable.editors.TextEditor;
export class MaxLengthEditor extends TextEditor {
  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: Handsontable.CellProperties
  ): void {
    super.prepare(row, col, prop, td, originalValue, cellProperties);
    const maxLength = (cellProperties as { maxLength?: number }).maxLength;
    if (this.TEXTAREA) {
      const textarea = this.TEXTAREA as HTMLTextAreaElement;
      if (maxLength) {
        textarea.maxLength = maxLength;
      } else {
        textarea.removeAttribute('maxLength');
      }
    }
  }
}
/**
 * Get nested headers configuration for 3-line format Handsontable
 * @returns Nested headers array for 3-line format (columns 0-12)
 */
export const get3LineNestedHeaders = () => {
  return [
    [
      { label: '', rowspan: 2 }, // col 0: STT
      '', // col 1: ステータス
      '',
      '', // col 2: プラント
      { label: '時間', colspan: 3 }, // col 3 (spans to 5)
      '', // col 4 (covered by colspan)
      { label: '数量', colspan: 3 }, // col 8 (spans to 10)
      { label: '商品', colspan: 2 }, // col 11 (spans to 12)
      '', // col 12 (covered by colspan)
    ],
    [
      '', // col 0: STT
      'ｽﾃｰﾀｽ', // col 1
      'プラント', // col 2
      '打設日', // col 3
      '希望', // col 4
      '可能', // col 5
      '可能時間補助', // col 6
      '打設箇所', // col 7
      '数量', // col 8
      '接尾辞', // col 9
      '単位', // col 10
      '種類', // col 11
      '内容', // col 12
    ],
  ];
};

/**
 * Get columns for 3-line format (Line 1 only - columns 0-12)
 * This ensures columns stay in sync with 1-line format
 * Note: In 3-line format, column 0 is STT (not in 1-line), so 1-line columns are shifted by 1 index
 * Mapping: 3-line col 0 = STT, 3-line col 1 = 1-line col 0, 3-line col 2 = 1-line col 1, etc.
 * @param plantOptions - Plant options for autocomplete column
 * @param witnessCompanyOptions - Witness company options for autocomplete column
 * @returns Array of column definitions for Line 1 (columns 0-12)
 */
export const get3LineColumns = (
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = []
) => {
  const allColumns = getHandsontableColumns(plantOptions, witnessCompanyOptions);
  // Extract columns 0-11 from 1-line format (will be mapped to 3-line columns 1-12)
  const oneLineColumnsFor3Line = allColumns.slice(0, 12);

  // Create 3-line columns array: [STT column, ...1-line columns 0-11]
  const threeLineColumns = [
    // Column 0: STT (not in 1-line format)
    {
      title: '',
      type: 'text' as const,
      readOnly: true,
      className: 'htCenter htMiddle',
      width: 40,
    },
    // Columns 1-12: Map from 1-line columns 0-11
    ...oneLineColumnsFor3Line.map((col, oneLineIndex) => {
      const baseCol = { ...col };
      // Update data property to match 3-line column index (shifted by 1)
      const threeLineDataIndex = oneLineIndex + 1;

      // Column 1 (3-line) = ステータス (1-line column 0) - increase width
      if (threeLineDataIndex === 1) {
        return {
          ...baseCol,
          data: threeLineDataIndex,
          width: 80,
          minWidth: 70,
        };
      }

      // Column 2 (3-line) = プラント (1-line column 1) - ensure autocomplete properties are correct
      if (threeLineDataIndex === 2) {
        return {
          ...baseCol,
          data: threeLineDataIndex,
          width: baseCol.width || 150,
          minWidth: 120,
        };
      }

      // Column 3 (3-line) = 打設日 (1-line column 2) - add datepickerRenderer for proper display
      if (threeLineDataIndex === 3) {
        return {
          ...baseCol,
          data: threeLineDataIndex,
          renderer: datepickerRenderer,
        };
      }

      // Return column with updated data index and ensure width properties are set
      return {
        ...baseCol,
        data: threeLineDataIndex,
        width: baseCol.width || 100,
        minWidth: ('minWidth' in baseCol ? baseCol.minWidth : undefined) || baseCol.width || 60,
      };
    }),
  ];

  return threeLineColumns;
};

/**
 * Type definition for wrapped row column in 3-line format
 */
export type WrappedRowColumn =
  | {
      key: string | number;
      label: string;
      props?: Record<string, unknown>;
    }
  | undefined;

/**
 * Create wrapped row column table for 3-line format
 * Defines the structure for Line 2 and Line 3 columns
 * @param _plantOptions - Plant options (not used but kept for consistency)
 * @param witnessCompanyOptions - Witness company options for autocomplete column
 * @returns Array of wrapped row column definitions
 */
export const createWrappedRowColumnTable = (
  _plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
): WrappedRowColumn[][] => {
  return [
    [
      {
        key: 'vehicleSize',
        label: '車両',
        props: {
          type: 'text',
          source: Object.values(EPlanVehicleSizeLabel),
          editor: createDropdownEditorClass(
            DropdownEditor as unknown as ComponentType<ComponentEditorProps>,
            (_col: number) => Object.values(EPlanVehicleSizeLabel),
            (_col: number) => false
          ),
          renderer: dropdownRenderer,
          canEnterAnotherValue: false,
          strict: true,
          allowInvalid: false,
        },
      },
      {
        key: 'precedingMaterialType',
        label: '先行材 種類',
        props: {
          type: 'text',
          source: Object.values(EPlanMaterialTypeLabel),
          editor: createDropdownEditorClass(
            DropdownEditor as unknown as ComponentType<ComponentEditorProps>,
            (_col: number) => Object.values(EPlanMaterialTypeLabel),
            (_col: number) => false
          ),
          renderer: dropdownRenderer,
          canEnterAnotherValue: false,
          strict: true,
          allowInvalid: false,
        },
      },
      {
        key: 'precedingMaterialQuantity',
        label: '先行材 数量',
        props: { type: 'numeric', allowInvalid: true },
      },
      {
        key: 'pitchHope',
        label: 'ピッチ希望',
        props: {
          type: 'text',
          editor: 'maxLengthEditor',
          maxLength: PLAN_FIELDS.PITCH_HOPE.maxLength,
          allowInvalid: true,
          wordWrap: true,
        },
      },
      {
        key: 'pitchPossible',
        label: 'ピッチ可能',
        props: {
          type: 'text',
          editor: 'maxLengthEditor',
          maxLength: PLAN_FIELDS.PITCH_POSSIBLE.maxLength,
          allowInvalid: true,
          wordWrap: true,
        },
      },
      {
        key: 'placingMethod',
        label: '打設方法',
        props: {
          type: 'text',
          source: Object.values(EPlanPlacingMethodLabel),
          editor: createDropdownEditorClass(
            DropdownEditor as unknown as ComponentType<ComponentEditorProps>,
            (_col: number) => Object.values(EPlanPlacingMethodLabel),
            (_col: number) => true
          ),
          renderer: dropdownRenderer,
          canEnterAnotherValue: true,
          strict: true,
          allowInvalid: true,
          maxLength: PLAN_FIELDS.PLACING_METHOD.maxLength,
        },
      },
      {
        key: 'witnessCompanyId',
        label: '立会会社',
        props: {
          type: 'autocomplete',
          source: witnessCompanyOptions.map((opt) => String(opt.label).trim()),
          editor: createAutocompleteEditorClass(
            AutocompleteEditor as unknown as ComponentType<ComponentEditorProps>,
            (_col: number) => {
              return witnessCompanyOptions;
            }
          ),
          strict: false,
          filter: true,
          allowInvalid: true,
          allowEmpty: true,
        },
      },
      {
        key: 'witnessName',
        label: '氏名',
        props: {
          type: 'text',
          editor: 'maxLengthEditor',
          maxLength: PLAN_FIELDS.WITNESS_NAME.maxLength,
        },
      },
      {
        key: 'witnessTel',
        label: '連絡先',
        props: {
          type: 'text',
          editor: 'maxLengthEditor',
          maxLength: PLAN_FIELDS.WITNESS_TEL.maxLength,
        },
      },
    ],
    [
      {
        key: 'remarks',
        label: '備考 社内',
        props: {
          type: 'text',
          editor: 'maxLengthEditor',
          maxLength: PLAN_FIELDS.REMARKS.maxLength,
          allowInvalid: true,
        },
      },
      {
        key: 'remarksForClient',
        label: '備考 ゼネコン',
        props: {
          type: 'text',
          editor: 'maxLengthEditor',
          maxLength: PLAN_FIELDS.REMARKS_FOR_CLIENT.maxLength,
          allowInvalid: true,
        },
      },
    ],
  ];
};
